import React from 'react';
import { Field, Area, StringList, Repeater, Card } from './fields';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

const set = (obj, path, value) => {
  const keys = path.split('.');
  const clone = JSON.parse(JSON.stringify(obj));
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
  cur[keys[keys.length - 1]] = value;
  return clone;
};

export const SiteSettingsEditor = ({ content, onChange }) => {
  const s = content.siteSettings || {};
  const up = (path, v) => onChange(set(content, `siteSettings.${path}`, v));
  return (
    <div className="space-y-6">
      <Card title="Company Details" description="Business name, phone, email and address shown across the site.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company Name" value={s.companyName} onChange={(v) => up('companyName', v)} />
          <Field label="Full Company Name" value={s.companyFull} onChange={(v) => up('companyFull', v)} />
        </div>
        <Field label="Tagline" value={s.tagline} onChange={(v) => up('tagline', v)} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Phone (display)" value={s.phone} onChange={(v) => up('phone', v)} />
          <Field label="Phone (dial, digits only)" value={s.phoneRaw} onChange={(v) => up('phoneRaw', v)} />
          <Field label="Fax" value={s.fax} onChange={(v) => up('fax', v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Email" value={s.email} onChange={(v) => up('email', v)} />
          <Field label="Address" value={s.address} onChange={(v) => up('address', v)} />
          <Field label="City / State / Zip" value={s.city} onChange={(v) => up('city', v)} />
        </div>
        <Field label="Emergency Note" value={s.emergencyNote} onChange={(v) => up('emergencyNote', v)} />
      </Card>
      <Card title="Business Hours">
        <Repeater items={s.hours || []} onChange={(v) => up('hours', v)} template={{ day: '', time: '' }}
          columns={[{ key: 'day', label: 'Day(s)' }, { key: 'time', label: 'Hours' }]} />
      </Card>
      <Card title="Energy Rebate Banner" description="Shown as a highlighted banner on the homepage.">
        <div className="flex items-center gap-3">
          <Switch checked={!!content.rebateBanner?.enabled} onCheckedChange={(v) => onChange(set(content, 'rebateBanner.enabled', v))} />
          <Label className="text-slate-700 text-sm font-semibold">Show rebate banner on homepage</Label>
        </div>
        <Field label="Heading" value={content.rebateBanner?.heading} onChange={(v) => onChange(set(content, 'rebateBanner.heading', v))} />
        <Area label="Text" value={content.rebateBanner?.text} onChange={(v) => onChange(set(content, 'rebateBanner.text', v))} />
      </Card>
    </div>
  );
};

export const HomeEditor = ({ content, onChange }) => {
  const h = content.homeContent || {};
  const up = (path, v) => onChange(set(content, `homeContent.${path}`, v));
  return (
    <div className="space-y-6">
      <Card title="Hero Slides" description="Rotating banners at the top of the homepage.">
        <Repeater items={h.heroSlides || []} onChange={(v) => up('heroSlides', v)} template={{ kicker: '', title: '', image: '' }}
          columns={[{ key: 'kicker', label: 'Small text (kicker)' }, { key: 'title', label: 'Headline' }, { key: 'image', label: 'Image URL' }]} />
        <StringList label="Hero Bullet Points" items={h.heroBullets || []} onChange={(v) => up('heroBullets', v)} />
      </Card>
      <Card title="Why Choose Us">
        <Field label="Heading" value={h.whyChoose?.heading} onChange={(v) => up('whyChoose.heading', v)} />
        <Area label="Paragraph" value={h.whyChoose?.paragraph} rows={4} onChange={(v) => up('whyChoose.paragraph', v)} />
        <Repeater label="Feature Blocks" items={h.whyChoose?.features || []} onChange={(v) => up('whyChoose.features', v)} template={{ icon: 'Wrench', title: '', text: '' }}
          columns={[{ key: 'icon', label: 'Icon (Clock, Wrench, ShieldCheck...)' }, { key: 'title', label: 'Title' }, { key: 'text', label: 'Text', type: 'area' }]} />
      </Card>
      <Card title="Services (Home grid)">
        <Repeater items={h.services || []} onChange={(v) => up('services', v)} template={{ icon: 'Snowflake', title: '', text: '', path: '/residential' }}
          columns={[{ key: 'icon', label: 'Icon' }, { key: 'title', label: 'Title' }, { key: 'text', label: 'Text', type: 'area' }, { key: 'path', label: 'Link path' }]} />
      </Card>
      <Card title="Stats Band">
        <Repeater items={h.stats || []} onChange={(v) => up('stats', v)} template={{ value: '', label: '' }}
          columns={[{ key: 'value', label: 'Value' }, { key: 'label', label: 'Label' }]} />
      </Card>
      <Card title="Testimonials">
        <Repeater items={h.testimonials || []} onChange={(v) => up('testimonials', v)} template={{ name: '', location: '', text: '' }}
          columns={[{ key: 'name', label: 'Name' }, { key: 'location', label: 'Location' }, { key: 'text', label: 'Quote', type: 'area' }]} />
      </Card>
      <Card title="FAQs">
        <Repeater items={content.faqs || []} onChange={(v) => onChange(set(content, 'faqs', v))} template={{ q: '', a: '' }}
          columns={[{ key: 'q', label: 'Question' }, { key: 'a', label: 'Answer', type: 'area' }]} />
      </Card>
    </div>
  );
};

const PAGE_KEYS = [
  { key: 'residential', name: 'Residential' },
  { key: 'commercial', name: 'Commercial' },
  { key: 'fabrication', name: 'Shop Fabrication' },
  { key: 'ductSealing', name: 'Duct Sealing' }
];

export const PagesEditor = ({ content, onChange }) => {
  const pc = content.pageContent || {};
  const up = (pk, field, v) => onChange(set(content, `pageContent.${pk}.${field}`, v));
  return (
    <div className="space-y-6">
      {PAGE_KEYS.map(({ key, name }) => {
        const p = pc[key] || {};
        return (
          <Card key={key} title={name} description={`Content for the ${name} service page.`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Title" value={p.title} onChange={(v) => up(key, 'title', v)} />
              <Field label="Subtitle" value={p.subtitle} onChange={(v) => up(key, 'subtitle', v)} />
            </div>
            <Field label="Image URL" value={p.image} onChange={(v) => up(key, 'image', v)} />
            <Area label="Intro" value={p.intro} onChange={(v) => up(key, 'intro', v)} />
            <StringList label="Body Paragraphs" items={p.body || []} onChange={(v) => up(key, 'body', v)} />
            <StringList label="Services Offered" items={p.services || []} onChange={(v) => up(key, 'services', v)} />
            <Area label="Note / Callout" value={p.note} onChange={(v) => up(key, 'note', v)} />
          </Card>
        );
      })}
    </div>
  );
};

export const AreasEditor = ({ content, onChange }) => (
  <Card title="Service Areas" description="SEO location pages. The 'slug' becomes the page URL (e.g. cortez-co).">
    <Repeater items={content.serviceAreas || []} onChange={(v) => onChange(set(content, 'serviceAreas', v))}
      template={{ slug: '', city: '', state: 'CO', title: '', subtitle: '', intro: '', body: [], services: [], landmarks: '' }}
      columns={[
        { key: 'slug', label: 'URL Slug' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'title', label: 'Page Title' },
        { key: 'subtitle', label: 'Subtitle' },
        { key: 'intro', label: 'Intro', type: 'area' },
        { key: 'body', label: 'Body Paragraphs', type: 'stringlist' },
        { key: 'services', label: 'Services', type: 'stringlist' },
        { key: 'landmarks', label: 'Landmarks / Coverage', type: 'area' }
      ]} />
  </Card>
);

export const ListsEditor = ({ content, onChange }) => (
  <div className="space-y-6">
    <Card title="Team Members" description="Upload a photo for each team member, or leave blank to show their initials.">
      <Repeater items={content.teamMembers || []} onChange={(v) => onChange(set(content, 'teamMembers', v))} template={{ name: '', role: '', bio: '', initials: '', image: '' }}
        columns={[{ key: 'image', label: 'Photo', type: 'image' }, { key: 'name', label: 'Name' }, { key: 'role', label: 'Role' }, { key: 'initials', label: 'Initials (fallback)' }, { key: 'bio', label: 'Bio', type: 'area' }]} />
    </Card>
    <Card title="Photo Gallery">
      <Repeater items={content.galleryImages || []} onChange={(v) => onChange(set(content, 'galleryImages', v))} template={{ src: '', caption: '' }}
        columns={[{ key: 'src', label: 'Image URL', type: 'image' }, { key: 'caption', label: 'Caption' }]} />
    </Card>
  </div>
);

export const CareersEditor = ({ content, onChange }) => (
  <Card title="Careers Page" description="Paste any HTML here — text, links, and embedded application widgets/iframes are supported. This is displayed on the Careers page.">
    <Area label="Careers HTML" value={content.careersHtml} rows={16} onChange={(v) => onChange(set(content, 'careersHtml', v))} placeholder="<h2>Join our team</h2><p>...</p>" />
    <div>
      <p className="text-sm font-semibold text-slate-700 mb-2">Live Preview</p>
      <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 careers-html" dangerouslySetInnerHTML={{ __html: content.careersHtml || '<p class="text-slate-400">Nothing to preview yet.</p>' }} />
    </div>
  </Card>
);
